import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  SecurityIssue,
  IssuesByGroup,
  IssuesBySystemInstance,
  TaxonomyPillar,
  TaxonomyCategory,
  TaxonomyMatrixCell,
  TaxonomyMatrixFilters,
  TopAsset,
  OpenVulnerabilitiesResponse,
  ResolvingTrend,
  SLAResponse,
  MTTRRecord,
  BaselineData,
  ReportFilters,
  SingleViewFilters,
  AssetFilters,
  ApplicationSecureSummary,
  PaginatedResponse,
  HackerOneFilters,
  HackerOneRecord,
  XmcoFilters,
  XmcoRecord,
  CspmFilters,
  OrcaFilters,
  FalconSensorCoverageFilters,
  FalconSensorCoverageRecord,
  CrowdstrikeRecord,
  OrcaAlertRecord,
  OrcaAttackPathRecord,
  OrcaVulnerabilityRecord,
  OrcaExternalEndpointRecord,
  ComplianceFilters,
  ComplianceControl,
  ComplianceTeamSummary,
  ComplianceControlRollup,
  ComplianceMatrixCell,
  ComplianceRemediationControl,
  ComplianceRemediation,
  FalconSystemDetailResponse,
  ComplianceAlertRecord,
  DomainsVerificationFilters,
  DomainsVerificationRecord,
  DomainsVerificationSummary,
  NonexistentDomainRecord,
  GhasRolloutFilters,
  GhasRolloutFilterOptions,
  OrgRolloutSummary,
  OrgRequirementBreakdown,
  RepoCompliance,
  PipelineRun,
  ScheduleInfo,
  EltApiResponse,
  EltRequestBody,
  EltExperienceRow,
  EltMetricsSnapshotRow,
  EltTimeseriesPoint,
  EltDateBoundsRow,
  EltDateRangeFilters,
  SloTrendTimeseriesPoint,
  AtRiskRow,
  SloCommitmentsResponse,
  SloCommitmentRecord,
  SloCommitmentEvent,
  XpLockResult,
  CommitTeamRequest,
  ScorecardCompany,
  ScorecardFactor,
  ScorecardIssue,
  ScorecardIssueFilters,
  GitHubActionsFilters,
  ScannersCoverageRecord,
  ScannersByTeamRecord,
  ScannersCoverageChartData,
  ActionsOverviewRecord,
  SchemaGraph,
  CallerIdentity,
  QuiesceServiceUnconfiguredError,
  QuiesceCategory,
  QuiesceReason,
  QuiesceReasonCode,
  QuiesceReasonsListFilters,
  QuiesceFindingLink,
  QuiesceEvent,
  FindingRef,
  QuiescedFinding,
  QuiescedFindingsFilters,
  SecurityControlsFilters,
  SecurityControlCatalog,
  SecurityControlsMatrix,
  SecurityControlAlertRecord,
  SecurityControlCoverage,
  SecurityControlsInstances,
  SecurityControlsTaxonomyInstances,
  SecurityControlsTaxonomyMatrix,
  FalconControlScore,
  FalconAttestationSubmitRequest,
} from './types';

type ServerPaginated<T> = T & {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  /** Free-text cross-column search (security-insights-backend's
   * service/searchAndSort.ts). Respected by `getSingleView`, `getTopAssets`,
   * `getIssuesByExperience`, `getIssuesByTeam`, `getIssuesByCve`, and
   * `getIssuesByIssueType` — the six calls `SingleViewPage` makes. Passing
   * it to any other call is a silent no-op server-side. */
  search?: string;
};

export class SecurityInsightsClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('security-insights');
  }

  private async get<T>(path: string): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!response.ok) {
      throw await this.requestError(response);
    }
    return response.json() as Promise<T>;
  }

  // Surfaces the backend's `{ error }` message (e.g. the proxy's "could not
  // authenticate to security-pipeline" 502) when there is one, falling back
  // to the bare status string.
  private async requestError(response: Response): Promise<Error> {
    const parsed = await response.json().catch(() => undefined);
    return new Error(
      parsed?.error ||
        `Request failed: ${response.status} ${response.statusText}`,
    );
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}`,
      );
    }
    return response.json() as Promise<T>;
  }

  // POST/DELETE helper for mutation endpoints that return meaningful
  // validation error messages (e.g. quiesce duration bounds) in a JSON
  // `{error}` body. Unlike `post()`, this surfaces that message verbatim so
  // the UI can show it inline instead of a generic HTTP status string.
  private async mutate<T>(
    path: string,
    method: 'POST' | 'DELETE',
    body?: unknown,
  ): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const parsed = await response.json().catch(() => undefined);
      // security-pipeline's quiesce-reason team-ownership 403 includes
      // `owning_team` (and `caller`) alongside `detail` — surface which team
      // actually owns the finding rather than the generic "does not own this
      // finding" detail, since the caller has no way to know that team's
      // name otherwise. Scoped to the quiesce-reason path: `owning_team` is
      // specific to that ownership model, and the wording below only makes
      // sense for a quiesce/lift action — other 403s (e.g. commitTeamEta's
      // lead-or-membership check) must fall through to the generic `detail`
      // branch below even if they happen to also carry an `owning_team`-like
      // field.
      if (parsed?.owning_team && path.includes('/quiesced-findings/reasons')) {
        // Shared by both quiesce (POST) and lift (DELETE) — word the message
        // for whichever action actually failed.
        const action = method === 'DELETE' ? 'lift the quiesce on' : 'quiesce';
        const callerSuffix = parsed?.caller
          ? ` (signed in as ${parsed.caller})`
          : '';
        throw new Error(
          `You can't ${action} this finding — it belongs to team "${parsed.owning_team}", which you're not a member of${callerSuffix}.`,
        );
      }
      // Our own proxy's 401/403/503 bodies use `{ error }`; security-pipeline's
      // proxied-through bodies (400/403/404/409/500) use `{ detail }`
      // (see security-pipeline's `writeErr`, internal/server/app.go). Check
      // both rather than picking one and silently losing the other's message.
      throw new Error(
        parsed?.error || parsed?.detail || `Request failed: ${response.status}`,
      );
    }
    // A handful of these callers (e.g. unlockXpGroup) proxy straight through
    // to security-pipeline via proxyMutation, which forwards the upstream
    // status/body verbatim — a `204 No Content` (or any empty body) success
    // response has nothing for `.json()` to parse, and it throws rather than
    // returning `undefined`, on an otherwise-successful request.
    const text = (await response.text()).trim();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  // Filter endpoints
  async getExperiences(): Promise<string[]> {
    return this.get('/filters/experiences');
  }

  async getTeams(): Promise<string[]> {
    return this.get('/filters/teams');
  }

  async getTeamsInExperiences(): Promise<Record<string, string[]>> {
    return this.get('/filters/teams-in-experiences');
  }

  async getSystems(): Promise<string[]> {
    return this.get('/filters/systems');
  }

  /** System name -> the deployed-instance names registered under it. */
  async getDeployedInstancesBySystem(): Promise<Record<string, string[]>> {
    return this.get('/filters/deployed-instances');
  }

  async getOrgs(): Promise<string[]> {
    return this.get('/filters/orgs');
  }

  async getRepos(): Promise<
    Array<{ full_name: string; catalog_info_owner: string; repo_name: string }>
  > {
    return this.get('/filters/repos');
  }

  async getToolNames(): Promise<string[]> {
    return this.get('/filters/tool-names');
  }

  async getSeverities(): Promise<string[]> {
    return this.get('/filters/severities');
  }

  async getIssueTypes(): Promise<string[]> {
    return this.get('/filters/issue-types');
  }

  // Taxonomy vocabulary. Unlike the other filter endpoints these return
  // {code,label} pairs — the findings columns store codes, the UI shows
  // labels, and the mapping lives server-side in the reference tables.
  async getPillars(): Promise<TaxonomyPillar[]> {
    return this.get('/filters/pillars');
  }

  async getCategories(): Promise<TaxonomyCategory[]> {
    return this.get('/filters/categories');
  }

  // Single View endpoints
  async getSingleView(
    filters: ServerPaginated<SingleViewFilters>,
  ): Promise<PaginatedResponse<SecurityIssue>> {
    return this.post('/single-view', filters);
  }

  async exportSingleViewCsv(
    filters: SingleViewFilters & { search?: string },
  ): Promise<void> {
    await this.downloadCsv(
      '/single-view/export',
      filters,
      'security_issues.csv',
    );
  }

  async getIssuesByExperience(
    filters: SingleViewFilters & { search?: string },
  ): Promise<IssuesByGroup[]> {
    return this.post('/single-view/by-experience', filters);
  }

  async getIssuesByTeam(
    filters: SingleViewFilters & { search?: string },
  ): Promise<IssuesByGroup[]> {
    return this.post('/single-view/by-team', filters);
  }

  async getIssuesByIssueType(
    filters: SingleViewFilters & { search?: string },
  ): Promise<IssuesByGroup[]> {
    return this.post('/single-view/by-issue-type', filters);
  }

  async getIssuesByCve(
    filters: SingleViewFilters & { search?: string },
  ): Promise<IssuesByGroup[]> {
    return this.post('/single-view/by-cve', filters);
  }

  async getIssuesByPillar(
    filters: SingleViewFilters,
  ): Promise<IssuesByGroup[]> {
    return this.post('/single-view/by-pillar', filters);
  }

  async getIssuesByCategory(
    filters: SingleViewFilters,
  ): Promise<IssuesByGroup[]> {
    return this.post('/single-view/by-category', filters);
  }

  /** Systems with their Deployed Instances nested under them, in one response. */
  async getIssuesBySystemInstance(
    filters: SingleViewFilters & { search?: string },
  ): Promise<IssuesBySystemInstance[]> {
    return this.post('/single-view/by-system-instance', filters);
  }

  /** Secure / Not secure System counts for the Posture Report's pie chart. */
  async getApplicationsSummary(
    filters: SingleViewFilters,
  ): Promise<ApplicationSecureSummary> {
    return this.post('/single-view/applications-summary', filters);
  }

  /** Taxonomy × org rollup backing the Security Posture Report's three levels. */
  async getTaxonomyMatrix(
    filters: TaxonomyMatrixFilters,
  ): Promise<TaxonomyMatrixCell[]> {
    return this.post('/single-view/matrix', filters);
  }

  // ELT Vulnerability Dashboard — see /security-insights/elt-vulnerabilities
  // route in Router.tsx. Every endpoint accepts an optional selectedDate
  // (YYYY-MM-DD); when omitted, the server resolves to the latest available
  // metric_date and echoes that back in the response envelope so the UI
  // can label charts accurately.
  async getEltExperiences(
    body: EltRequestBody = {},
  ): Promise<EltApiResponse<EltExperienceRow>> {
    return this.post('/elt-vulnerabilities/experiences', body);
  }

  async getEltMetricsSnapshot(
    body: EltRequestBody = {},
  ): Promise<EltApiResponse<EltMetricsSnapshotRow>> {
    return this.post('/elt-vulnerabilities/metrics-snapshot', body);
  }

  async getEltOpenTimeseries(
    body: EltRequestBody = {},
  ): Promise<EltApiResponse<EltTimeseriesPoint>> {
    return this.post('/elt-vulnerabilities/open-timeseries', body);
  }

  async getEltDateBounds(
    body: EltRequestBody = {},
  ): Promise<EltApiResponse<EltDateBoundsRow>> {
    return this.post('/elt-vulnerabilities/date-bounds', body);
  }

  /**
   * Generic, client-filterable date bounds/latest-date over
   * finding_metrics_daily — the counterpart to getEltDateBounds (which is
   * the ELT Vulnerability Dashboard's own locked endpoint and ignores its
   * body). Pass whatever combination of selectedPillar/selectedCategory/
   * selectedTool/selectedSeverities your report needs; omit all four for
   * unfiltered bounds. EltSloTrendPage calls this with
   * `{ selectedPillar: 'vulnerability', selectedSeverities: ['critical', 'high'] }`
   * hardcoded in its own code rather than sharing getEltDateBounds's wider,
   * actively-changing lock — the severity scope matches its own locked
   * slo-trend queries so the resolved date always has matching rows there.
   */
  async getEltDateRange(
    filters: EltDateRangeFilters = {},
  ): Promise<EltApiResponse<EltDateBoundsRow>> {
    return this.post('/elt-vulnerabilities/date-range', filters);
  }

  // SLO Compliance Trend page — team-granularity daily-only endpoints.
  async getSloTrendTimeseries(
    body: EltRequestBody = {},
  ): Promise<EltApiResponse<SloTrendTimeseriesPoint>> {
    return this.post('/elt-vulnerabilities/slo-trend/timeseries', body);
  }

  async getSloTrendAtRisk(
    body: EltRequestBody = {},
  ): Promise<EltApiResponse<AtRiskRow>> {
    return this.post('/elt-vulnerabilities/slo-trend/at-risk', body);
  }

  // SLO Commitments & XP Lock-In — see security-pipeline's
  // docs/SLO_COMMITMENTS_DESIGN.md. Reads go through Backstage's own
  // Postgres role (direct-read routes, like the reasons report below);
  // commit/lock/unlock proxy to security-pipeline, the system of record.
  async getSloCommitments(): Promise<SloCommitmentsResponse> {
    return this.post('/slo-commitments', {});
  }

  async commitTeamEta(
    teamSlug: string,
    params: CommitTeamRequest,
  ): Promise<SloCommitmentRecord> {
    return this.mutate(
      `/slo-commitments/${encodeURIComponent(teamSlug)}/commit`,
      'POST',
      params,
    );
  }

  async getSloCommitmentHistory(
    teamSlug: string,
  ): Promise<SloCommitmentEvent[]> {
    // This route proxies straight through to security-pipeline (see
    // sloCommitments.ts's `GET /:teamSlug/history` — a raw pass-through, not
    // one of this backend's own direct-read routes), so unlike
    // getReasonHistory's `{ data: [...] }` (a shape this backend controls),
    // the response shape here isn't pinned down anywhere in this repo.
    // Accept either a bare array or a `{ data: [...] }` wrapper rather than
    // assuming one and breaking this dialog if the upstream shape differs.
    const result = await this.get<
      SloCommitmentEvent[] | { data: SloCommitmentEvent[] }
    >(`/slo-commitments/${encodeURIComponent(teamSlug)}/history`);
    return Array.isArray(result) ? result : result.data;
  }

  // Returns the point-in-time commitment snapshot security-pipeline just
  // recorded on the 'locked' audit event — NOT an XpLockRecord (that's the
  // live lock-state shape returned by getSloCommitments, a different route).
  async lockXpGroup(experience: string): Promise<XpLockResult> {
    return this.mutate(
      `/xp-groups/${encodeURIComponent(experience)}/lock`,
      'POST',
    );
  }

  async unlockXpGroup(experience: string, note: string): Promise<void> {
    await this.mutate(
      `/xp-groups/${encodeURIComponent(experience)}/unlock`,
      'POST',
      { note },
    );
  }

  async exportAssetsCsv(
    filters: AssetFilters & { search?: string },
  ): Promise<void> {
    await this.downloadCsv('/single-view/export-assets', filters, 'assets.csv');
  }

  async exportAssetDetailsCsv(filters: AssetFilters): Promise<void> {
    await this.downloadCsv(
      '/single-view/export-asset-details',
      filters,
      'asset_issues.csv',
    );
  }

  private async downloadCsv(
    path: string,
    body: unknown,
    filename: string,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(
        `Export failed: ${response.status} ${response.statusText}`,
      );
    }

    const anyWindow = window as any;

    // Stream directly to disk when the File System Access API is available
    if (typeof anyWindow.showSaveFilePicker === 'function' && response.body) {
      try {
        const fileHandle = await anyWindow.showSaveFilePicker({
          suggestedName: filename,
          types: [
            { description: 'CSV file', accept: { 'text/csv': ['.csv'] } },
          ],
        });
        const writable = await fileHandle.createWritable();
        const reader = response.body.getReader();
        try {
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) await writable.write(value);
          }
        } finally {
          await writable.close();
          reader.releaseLock();
        }
        return;
      } catch {
        // User cancelled the picker or API unavailable — fall through to blob
      }
    }

    // Fallback: buffer into blob (works in all browsers)
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getTopAssets(
    filters: AssetFilters & { search?: string },
  ): Promise<PaginatedResponse<TopAsset>> {
    return this.post('/single-view/by-asset', filters);
  }

  async getAssetDetails(filters: AssetFilters): Promise<SecurityIssue[]> {
    return this.post('/single-view/asset-details', filters);
  }

  // License Compliance endpoints
  async getLicenseCompliance(
    filters: ServerPaginated<SingleViewFilters>,
  ): Promise<PaginatedResponse<SecurityIssue>> {
    return this.post('/license-compliance', filters);
  }

  async exportLicenseComplianceCsv(filters: SingleViewFilters): Promise<void> {
    await this.downloadCsv(
      '/license-compliance/export',
      filters,
      'license_compliance.csv',
    );
  }

  async getLicenseByExperience(
    filters: SingleViewFilters,
  ): Promise<IssuesByGroup[]> {
    return this.post('/license-compliance/by-experience', filters);
  }

  async getLicenseByTeam(filters: SingleViewFilters): Promise<IssuesByGroup[]> {
    return this.post('/license-compliance/by-team', filters);
  }

  async getLicenseByLicense(
    filters: SingleViewFilters,
  ): Promise<IssuesByGroup[]> {
    return this.post('/license-compliance/by-license', filters);
  }

  async getLicenseTopAssets(
    filters: AssetFilters,
  ): Promise<PaginatedResponse<TopAsset>> {
    return this.post('/license-compliance/by-asset', filters);
  }

  async getLicenseAssetDetails(
    filters: AssetFilters,
  ): Promise<SecurityIssue[]> {
    return this.post('/license-compliance/asset-details', filters);
  }

  async exportLicenseAssetsCsv(filters: AssetFilters): Promise<void> {
    await this.downloadCsv(
      '/license-compliance/export-assets',
      filters,
      'license_assets.csv',
    );
  }

  async exportLicenseAssetDetailsCsv(filters: AssetFilters): Promise<void> {
    await this.downloadCsv(
      '/license-compliance/export-asset-details',
      filters,
      'license_asset_issues.csv',
    );
  }

  // Report endpoints
  async getOpenVulnerabilities(
    filters: ReportFilters,
  ): Promise<OpenVulnerabilitiesResponse> {
    return this.post('/reports/open-vulnerabilities', filters);
  }

  async getResolvingTrends(filters: ReportFilters): Promise<ResolvingTrend[]> {
    return this.post('/reports/resolving-trends', filters);
  }

  async getSLA(filters: ReportFilters): Promise<SLAResponse> {
    return this.post('/reports/sla', filters);
  }

  async getMTTR(filters: ReportFilters): Promise<MTTRRecord[]> {
    return this.post('/reports/mttr', filters);
  }

  // Baseline endpoints
  async getBaselineRepos(): Promise<string[]> {
    return this.get('/baseline/repos');
  }

  async getBaselineData(repo: string): Promise<BaselineData> {
    return this.get(`/baseline/${encodeURIComponent(repo)}`);
  }

  // HackerOne endpoints
  async getHackerOneAssets(): Promise<string[]> {
    return this.get('/hackerone/filter/assets');
  }

  async getHackerOneReports(
    filters: ServerPaginated<HackerOneFilters>,
  ): Promise<PaginatedResponse<HackerOneRecord>> {
    return this.post('/hackerone/reports', filters);
  }

  async exportHackerOneCsv(filters: HackerOneFilters): Promise<void> {
    await this.downloadCsv(
      '/hackerone/reports/export',
      filters,
      'hackerone_reports.csv',
    );
  }

  // XMCO endpoints
  async getXmcoAssets(): Promise<string[]> {
    return this.get('/xmco/filter/assets');
  }

  async getXmcoSeverities(): Promise<string[]> {
    return this.get('/xmco/filter/severities');
  }

  async getXmcoStatuses(): Promise<string[]> {
    return this.get('/xmco/filter/statuses');
  }

  async getXmcoAlerts(
    filters: ServerPaginated<XmcoFilters>,
  ): Promise<PaginatedResponse<XmcoRecord>> {
    return this.post('/xmco/alerts', filters);
  }

  async exportXmcoCsv(filters: XmcoFilters): Promise<void> {
    await this.downloadCsv('/xmco/alerts/export', filters, 'xmco_alerts.csv');
  }

  // CSPM filter endpoints
  async getAwsAccountNames(): Promise<string[]> {
    return this.get('/cspm/filter/aws-account-names');
  }

  async getFalconAccountNames(): Promise<string[]> {
    return this.get('/cspm/filter/falcon-account-names');
  }

  async getFalconTeams(): Promise<string[]> {
    return this.get('/cspm/filter/falcon-teams');
  }

  async getFalconSensorCoverageSummary(
    filters?: Pick<
      FalconSensorCoverageFilters,
      'selectedAccountName' | 'selectedSensorStatus' | 'selectedTeam'
    >,
  ): Promise<Record<string, number>> {
    return this.post('/cspm/falcon-sensor-coverage/summary', filters ?? {});
  }

  async getFalconSensorCoverage(
    filters: FalconSensorCoverageFilters,
  ): Promise<PaginatedResponse<FalconSensorCoverageRecord>> {
    return this.post('/cspm/falcon-sensor-coverage', filters);
  }

  async getAwsRegions(): Promise<string[]> {
    return this.get('/cspm/filter/aws-regions');
  }

  async getOrcaCloudAccounts(): Promise<string[]> {
    return this.get('/cspm/filter/orca-cloud-accounts');
  }

  async getOrcaCloudProviders(): Promise<string[]> {
    return this.get('/cspm/filter/orca-cloud-providers');
  }

  async getOrcaExperiences(): Promise<string[]> {
    return this.get('/cspm/filter/orca-experiences');
  }

  async getOrcaTeams(experience?: string): Promise<string[]> {
    const params =
      experience && experience !== 'All'
        ? `?experience=${encodeURIComponent(experience)}`
        : '';
    return this.get(`/cspm/filter/orca-teams${params}`);
  }

  // CSPM data endpoints (server-side paginated)
  async getCrowdstrikeData(
    filters: ServerPaginated<CspmFilters>,
  ): Promise<PaginatedResponse<CrowdstrikeRecord>> {
    return this.post('/cspm/crowdstrike', filters);
  }

  async getOrcaAlerts(
    filters: ServerPaginated<OrcaFilters>,
  ): Promise<PaginatedResponse<OrcaAlertRecord>> {
    return this.post('/cspm/orca-alerts', filters);
  }

  async getOrcaAttackPaths(
    filters: ServerPaginated<OrcaFilters>,
  ): Promise<PaginatedResponse<OrcaAttackPathRecord>> {
    return this.post('/cspm/orca-attack-paths', filters);
  }

  async getOrcaVulnerabilities(
    filters: ServerPaginated<OrcaFilters>,
  ): Promise<PaginatedResponse<OrcaVulnerabilityRecord>> {
    return this.post('/cspm/orca-vulnerabilities', filters);
  }

  // CSPM export endpoints (streaming CSV)
  async exportCrowdstrikeCsv(filters: CspmFilters): Promise<void> {
    await this.downloadCsv(
      '/cspm/crowdstrike/export',
      filters,
      'crowdstrike_cspm.csv',
    );
  }

  async exportOrcaAlertsCsv(filters: OrcaFilters): Promise<void> {
    await this.downloadCsv(
      '/cspm/orca-alerts/export',
      filters,
      'orca_alerts.csv',
    );
  }

  async exportOrcaAttackPathsCsv(filters: OrcaFilters): Promise<void> {
    await this.downloadCsv(
      '/cspm/orca-attack-paths/export',
      filters,
      'orca_attack_paths.csv',
    );
  }

  async exportOrcaVulnerabilitiesCsv(filters: OrcaFilters): Promise<void> {
    await this.downloadCsv(
      '/cspm/orca-vulnerabilities/export',
      filters,
      'orca_vulnerabilities.csv',
    );
  }

  async getOrcaExternalEndpoints(
    filters: ServerPaginated<OrcaFilters>,
  ): Promise<PaginatedResponse<OrcaExternalEndpointRecord>> {
    return this.post('/cspm/orca-external-endpoints', filters);
  }

  async exportOrcaExternalEndpointsCsv(filters: OrcaFilters): Promise<void> {
    await this.downloadCsv(
      '/cspm/orca-external-endpoints/export',
      filters,
      'orca_external_endpoints.csv',
    );
  }

  // ── Compliance (Falcon Tiger framework) endpoints ─────────────────────────

  async getComplianceFrameworks(): Promise<string[]> {
    return this.get('/compliance/filter/frameworks');
  }

  async getComplianceTeams(framework?: string): Promise<string[]> {
    const params = framework
      ? `?framework=${encodeURIComponent(framework)}`
      : '';
    return this.get(`/compliance/filter/teams${params}`);
  }

  async getComplianceControls(
    framework?: string,
  ): Promise<ComplianceControl[]> {
    const params = framework
      ? `?framework=${encodeURIComponent(framework)}`
      : '';
    return this.get(`/compliance/filter/controls${params}`);
  }

  async getComplianceTeamSummary(
    filters: ComplianceFilters,
  ): Promise<ComplianceTeamSummary[]> {
    return this.post('/compliance/team-summary', filters);
  }

  async getComplianceControlRollup(
    filters: ComplianceFilters,
  ): Promise<ComplianceControlRollup[]> {
    return this.post('/compliance/controls', filters);
  }

  async getComplianceMatrix(
    filters: ComplianceFilters,
  ): Promise<ComplianceMatrixCell[]> {
    return this.post('/compliance/matrix', filters);
  }

  async getComplianceAlerts(
    filters: ServerPaginated<ComplianceFilters>,
  ): Promise<PaginatedResponse<ComplianceAlertRecord>> {
    return this.post('/compliance/alerts', filters);
  }

  async exportComplianceAlertsCsv(filters: ComplianceFilters): Promise<void> {
    await this.downloadCsv(
      '/compliance/alerts/export',
      filters,
      'compliance_alerts.csv',
    );
  }

  // ── Security Controls By Application endpoints ────────────────────────────

  /**
   * The 21-control catalog. Fetched from the backend rather than bundled into
   * the frontend so there is exactly one copy in the repo — the same file the
   * SQL rule predicates are built from.
   */
  async getSecurityControlCatalog(): Promise<SecurityControlCatalog> {
    return this.get('/security-controls/catalog');
  }

  /**
   * Applications x controls. `cells` carries only the failing pairs; an
   * assessable control with no cell for an application is passing.
   */
  async getSecurityControlsMatrix(
    filters: SecurityControlsFilters,
  ): Promise<SecurityControlsMatrix> {
    return this.post('/security-controls/matrix', filters);
  }

  /**
   * Applications the matrix can't assess (no component repo, or no repo with a
   * resolvable team), plus the total they're measured against. Takes no filters
   * — an application with no team has no Experience to filter on either.
   */
  async getSecurityControlCoverage(): Promise<SecurityControlCoverage> {
    return this.get('/security-controls/coverage-gaps');
  }

  /** Orca alerts behind one (application, control) cell. Server-side paginated. */
  async getSecurityControlAlerts(
    filters: ServerPaginated<SecurityControlsFilters>,
  ): Promise<PaginatedResponse<SecurityControlAlertRecord>> {
    return this.post('/security-controls/alerts', filters);
  }

  /**
   * Applications x the DB-backed finding taxonomy (Attack Paths, CSPM, ASPM,
   * Vulnerabilities) — the Taxonomy Matrix tab. `cells` carries only pairs
   * with open findings; the frontend reuses the `applications` array already
   * fetched from
   * `getSecurityControlsMatrix` for the row set, and `getPillars()`/
   * `getCategories()` for column vocabulary.
   */
  async getSecurityControlsTaxonomyMatrix(
    filters: SecurityControlsFilters,
  ): Promise<SecurityControlsTaxonomyMatrix> {
    return this.post('/security-controls/taxonomy-matrix', filters);
  }

  /**
   * The registered deployed instances under each in-scope System, plus their
   * failing Controls Matrix (V1) cells — what expanding a System row shows.
   * Fetched separately from `getSecurityControlsMatrix` so that a failure here
   * (e.g. a missing database grant) leaves the matrix itself intact.
   */
  async getSecurityControlsInstances(
    filters: SecurityControlsFilters,
  ): Promise<SecurityControlsInstances> {
    return this.post('/security-controls/instances', filters);
  }

  /**
   * The Controls Matrix V2 (taxonomy) counterpart of
   * `getSecurityControlsInstances`: the same instance list, with open-finding
   * counts per (instance, pillar, category).
   */
  async getSecurityControlsTaxonomyInstances(
    filters: SecurityControlsFilters,
  ): Promise<SecurityControlsTaxonomyInstances> {
    return this.post('/security-controls/taxonomy-instances', filters);
  }

  async exportSecurityControlAlertsCsv(
    filters: SecurityControlsFilters,
  ): Promise<void> {
    await this.downloadCsv(
      '/security-controls/alerts/export',
      filters,
      'security_controls_alerts.csv',
    );
  }

  /**
   * Confidence-engine rows for every scored control on one (team, system)
   * pair — everything the Attestation Form tab needs to show current
   * status. `GET /falcon/teams/:team/systems/:system` (SEC-1761/1762). An
   * empty array is a normal answer (nothing scored yet), not an error.
   */
  async getFalconTeamSystemScores(params: {
    team: string;
    system: string;
  }): Promise<FalconControlScore[]> {
    const { data } = await this.getFalconSystemDetail(
      params.team,
      params.system,
    );
    return data;
  }

  /**
   * Records a manual attestation. Proxied to security-pipeline
   * (`POST /falcon/attestations`) — `attester_email` is resolved
   * server-side from the authenticated Backstage caller, never sent from
   * here. Returns nothing meaningful to the caller by design: the response
   * body's shape is security-pipeline's own and not part of this backend's
   * contract, so callers should re-fetch `getFalconTeamSystemScores` to see
   * the effect of a successful submission.
   */
  async submitFalconAttestation(
    payload: FalconAttestationSubmitRequest,
  ): Promise<void> {
    await this.mutate('/falcon/attestations', 'POST', payload);
  }

  async getRemediableControls(
    framework: string,
  ): Promise<ComplianceRemediationControl[]> {
    return this.get(
      `/compliance/remediation/controls?framework=${encodeURIComponent(
        framework,
      )}`,
    );
  }

  // Returns null on 404 (no guide published for this control yet) so callers
  // can render the empty state instead of treating it as an error.
  async getRemediation(
    framework: string,
    controlId: string,
  ): Promise<ComplianceRemediation | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/compliance/remediation?framework=${encodeURIComponent(
        framework,
      )}&controlId=${encodeURIComponent(controlId)}`,
    );
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()) as ComplianceRemediation;
  }

  /**
   * The Falcon control numbers that have a published overview guide. The grid
   * renders "How to fix" only for these, so a link never opens an empty state.
   */
  async getFalconRemediationControls(framework: string): Promise<number[]> {
    return this.get(
      `/compliance/remediation/falcon/controls?framework=${encodeURIComponent(
        framework,
      )}`,
    );
  }

  /**
   * The overview guide for one Falcon Audit control, by control number 1-24.
   *
   * The lookup the Security Controls By Application grid uses. Unlike
   * `getRemediation`, which keys on an Orca framework position and so only
   * resolves for the 9 controls Orca observes, every one of the 21 active controls
   * has an overview guide — which is what lets the grid offer "How to fix" on
   * every row rather than only where an Orca rule happens to exist.
   *
   * Returns null on 404 (no overview published for this control yet) so callers
   * render an empty state instead of treating it as an error.
   */
  async getFalconRemediation(
    framework: string,
    controlNumber: number,
  ): Promise<ComplianceRemediation | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/compliance/remediation/falcon?framework=${encodeURIComponent(
        framework,
      )}&controlNumber=${encodeURIComponent(String(controlNumber))}`,
    );
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()) as ComplianceRemediation;
  }

  /**
   * The overview guide for one finding-taxonomy category, by
   * (pillarCode, categoryCode) — the Taxonomy Matrix's column-header link.
   *
   * No `framework` argument, unlike `getFalconRemediation`: "Security
   * Taxonomy" is the only framework this doc tree exists under.
   *
   * Returns null on 404 (no overview published for this category yet) so
   * callers render an empty state instead of treating it as an error.
   */
  async getTaxonomyRemediation(
    pillarCode: string,
    categoryCode: string,
  ): Promise<ComplianceRemediation | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/compliance/remediation/taxonomy?pillarCode=${encodeURIComponent(
        pillarCode,
      )}&categoryCode=${encodeURIComponent(categoryCode)}`,
    );
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()) as ComplianceRemediation;
  }

  // ── Falcon Compliance (confidence engine) endpoints ──────────────────────
  //
  // Distinct from the `/compliance/*` endpoints above, which read Orca's own
  // framework results. These read the SEC-1761 confidence engine: what we
  // know about a control, how sure we are, and what a team has attested.

  /**
   * The 21-control Falcon Tiger catalog. Needed by any Falcon surface that
   * renders control names or category headings — the score rows carry
   * neither, only `control_id`.
   *
   * Same payload as {@link getSecurityControlCatalog}: both routes project the
   * one vendored catalog through the backend's shared `toApiControl`. Kept as
   * its own method because the Falcon surfaces address the Falcon route, not
   * because the answer differs.
   */
  async getFalconControls(): Promise<SecurityControlCatalog> {
    return this.get('/falcon/controls');
  }

  /**
   * Every scored control for one (team, system), plus that System's catalog
   * metadata.
   *
   * Returns only controls the scoring step has written evidence for — often
   * none. Callers must left-join the result onto `getFalconControls()` and
   * render the remainder as Not assessed rather than treating a short list as
   * a short catalog.
   */
  async getFalconSystemDetail(
    team: string,
    system: string,
  ): Promise<FalconSystemDetailResponse> {
    return this.get(
      `/falcon/teams/${encodeURIComponent(team)}/systems/${encodeURIComponent(
        system,
      )}`,
    );
  }

  /**
   * The teams `POST /falcon/attestations` will accept for a System — those in
   * its owning Experience (`GET /falcon/systems/:system/attesting-teams`). An
   * attestation belongs to the System, so this, not the team's own assets, is
   * what decides who may file one. An empty array means nobody can (the System
   * has no owning Experience, or is not registered), not an error.
   */
  async getFalconAttestingTeams(system: string): Promise<string[]> {
    const { teams } = await this.get<{ teams: string[] }>(
      `/falcon/systems/${encodeURIComponent(system)}/attesting-teams`,
    );
    return teams;
  }

  async getDomainsOwners(): Promise<string[]> {
    return this.get('/domains-verification/owners');
  }

  async getDomainsVerificationSummary(
    filters: DomainsVerificationFilters,
  ): Promise<DomainsVerificationSummary> {
    return this.post('/domains-verification/summary', filters);
  }

  async getDomainsVerification(
    filters: ServerPaginated<DomainsVerificationFilters>,
  ): Promise<PaginatedResponse<DomainsVerificationRecord>> {
    return this.post('/domains-verification', filters);
  }

  async exportDomainsVerificationCsv(
    filters: DomainsVerificationFilters,
  ): Promise<void> {
    await this.downloadCsv(
      '/domains-verification/export',
      filters,
      'domains_verification.csv',
    );
  }

  async getNonexistentDomains(
    filters: DomainsVerificationFilters,
  ): Promise<NonexistentDomainRecord[]> {
    return this.post('/domains-verification/nonexistent', filters);
  }

  // ── GHAS Rollout endpoints ────────────────────────────────────────────────

  async getGhasOrgSummary(
    filters: GhasRolloutFilters,
  ): Promise<OrgRolloutSummary[]> {
    return this.post('/ghas-rollout/org-summary', filters);
  }

  async getGhasOrgRequirements(
    filters: GhasRolloutFilters,
  ): Promise<OrgRequirementBreakdown[]> {
    return this.post('/ghas-rollout/org-requirements', filters);
  }

  async getGhasRepoCompliance(
    filters: ServerPaginated<GhasRolloutFilters>,
  ): Promise<PaginatedResponse<RepoCompliance>> {
    return this.post('/ghas-rollout/repo-compliance', filters);
  }

  async getGhasRolloutFilterOptions(): Promise<GhasRolloutFilterOptions> {
    return this.get('/ghas-rollout/filter-options');
  }

  async exportGhasRepoCompliance(filters: GhasRolloutFilters): Promise<void> {
    await this.downloadCsv(
      '/ghas-rollout/repo-compliance/export',
      filters,
      'ghas_repo_compliance.csv',
    );
  }

  // ── GitHub Actions ──────────────────────────────────────────────────────────

  async getGitHubActionsLanguages(): Promise<string[]> {
    return this.get('/github-actions/filters/languages');
  }

  async getGitHubActionsRepos(filters: {
    selectedOrg?: string;
    selectedExperience?: string;
    selectedTeam?: string;
  }): Promise<string[]> {
    const params = new URLSearchParams();
    if (filters.selectedOrg) params.set('org', filters.selectedOrg);
    if (filters.selectedExperience)
      params.set('experience', filters.selectedExperience);
    if (filters.selectedTeam) params.set('team', filters.selectedTeam);
    const query = params.toString();
    return this.get(`/github-actions/filters/repos${query ? `?${query}` : ''}`);
  }

  async getScannersCoverage(
    filters: GitHubActionsFilters & { page?: number; pageSize?: number },
  ): Promise<PaginatedResponse<ScannersCoverageRecord>> {
    return this.post('/github-actions/scanners-coverage', filters);
  }

  async exportScannersCoverageCsv(
    filters: GitHubActionsFilters,
  ): Promise<void> {
    await this.downloadCsv(
      '/github-actions/scanners-coverage/export',
      filters,
      'scanners_coverage.csv',
    );
  }

  async getMissingScannersCoverage(
    filters: GitHubActionsFilters & { page?: number; pageSize?: number },
  ): Promise<PaginatedResponse<ScannersCoverageRecord>> {
    return this.post('/github-actions/scanners-coverage/missing', filters);
  }

  async exportMissingScannersCoverageCsv(
    filters: GitHubActionsFilters,
  ): Promise<void> {
    await this.downloadCsv(
      '/github-actions/scanners-coverage/missing/export',
      filters,
      'missing_scanners_coverage.csv',
    );
  }

  async getScannersByTeam(
    filters: GitHubActionsFilters,
  ): Promise<ScannersByTeamRecord[]> {
    return this.post('/github-actions/scanners-coverage/by-team', filters);
  }

  async getScannersCoverageChart(
    filters: GitHubActionsFilters,
  ): Promise<ScannersCoverageChartData | null> {
    return this.post('/github-actions/scanners-coverage/chart', filters);
  }

  async getActionsOverview(
    filters: GitHubActionsFilters & { page?: number; pageSize?: number },
  ): Promise<PaginatedResponse<ActionsOverviewRecord>> {
    return this.post('/github-actions/actions-overview', filters);
  }

  async exportActionsOverviewCsv(filters: GitHubActionsFilters): Promise<void> {
    await this.downloadCsv(
      '/github-actions/actions-overview/export',
      filters,
      'actions_overview.csv',
    );
  }

  // Status
  async getPipelineRuns(): Promise<PipelineRun[]> {
    return this.get('/status/pipeline-runs');
  }

  async getSchedules(): Promise<ScheduleInfo[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/status/schedules`);
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(
        detail?.error || `${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }

  // Schema & view dependencies
  async getSchema(): Promise<SchemaGraph> {
    return this.get('/schema');
  }

  async getTriggerPermission(): Promise<{ allowed: boolean }> {
    return this.get('/status/trigger-permission');
  }

  async triggerIngest(
    source: string,
    options?: { full?: boolean },
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = options?.full ? '?full=true' : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/status/trigger/${encodeURIComponent(source)}${query}`,
      { method: 'POST' },
    );
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(
        detail?.detail ||
          detail?.error ||
          `${response.status} ${response.statusText}`,
      );
    }
    // Drain the body so the connection can be reused by the agent pool.
    await response.text().catch(() => undefined);
  }

  // ── Security Scorecard endpoints ─────────────────────────────────────────────

  async getScorecardCompanies(): Promise<ScorecardCompany[]> {
    return this.get('/scorecard/companies');
  }

  async getScorecardFactors(company?: string): Promise<ScorecardFactor[]> {
    const params = company ? `?company=${encodeURIComponent(company)}` : '';
    return this.get(`/scorecard/factors${params}`);
  }

  async getScorecardIssues(
    filters: ScorecardIssueFilters & {
      page: number;
      pageSize: number;
      orderBy?: string;
      orderDirection?: 'asc' | 'desc';
    },
  ): Promise<{ data: ScorecardIssue[]; totalCount: number }> {
    return this.post('/scorecard/issues', filters);
  }

  async exportScorecardIssuesCsv(
    filters: ScorecardIssueFilters & {
      orderBy?: string;
      orderDirection?: 'asc' | 'desc';
    },
  ): Promise<void> {
    await this.downloadCsv(
      '/scorecard/issues/export',
      filters,
      'scorecard_issues.csv',
    );
  }

  async getScorecardFilterCompanies(): Promise<
    { domain: string; name: string }[]
  > {
    return this.get('/scorecard/filters/companies');
  }

  async getScorecardFilterFactors(): Promise<string[]> {
    return this.get('/scorecard/filters/factors');
  }

  async getScorecardFilterIssueTypes(): Promise<string[]> {
    return this.get('/scorecard/filters/issue-types');
  }

  async getScorecardFilterSeverities(): Promise<string[]> {
    return this.get('/scorecard/filters/severities');
  }

  async getScorecardFilterTeams(): Promise<string[]> {
    return this.get('/scorecard/filters/teams');
  }

  async getScorecardFilterGroupStatuses(): Promise<string[]> {
    return this.get('/scorecard/filters/group-statuses');
  }

  // ── Quiesce reasons (findings suppression) endpoints ─────────────────────

  async createQuiesceReason(params: {
    category: QuiesceCategory;
    reasonCode: string;
    description: string;
    compensatingControls: string;
    durationDays: number;
    findings: FindingRef[];
  }): Promise<QuiesceReason> {
    return this.mutate('/quiesced-findings/reasons', 'POST', {
      category: params.category,
      reason_code: params.reasonCode,
      description: params.description,
      compensating_controls: params.compensatingControls,
      duration_days: params.durationDays,
      findings: params.findings,
    });
  }

  /** v3 reason-code taxonomy (see `config/quiesce_reason_codes.yaml` in
   * security-pipeline), keyed by category. */
  async getReasonCodes(): Promise<{
    cant_fix: QuiesceReasonCode[];
    wont_fix: QuiesceReasonCode[];
  }> {
    return this.get('/quiesced-findings/reasons/reason-codes');
  }

  async attachFindings(
    reasonId: number,
    findings: FindingRef[],
  ): Promise<QuiesceFindingLink[]> {
    return this.mutate(
      `/quiesced-findings/reasons/${reasonId}/findings`,
      'POST',
      { findings },
    );
  }

  async unlinkFinding(
    reasonId: number,
    tool: string,
    srcId: string,
  ): Promise<void> {
    await this.mutate(
      `/quiesced-findings/reasons/${reasonId}/findings/${encodeURIComponent(
        tool,
      )}/${encodeURIComponent(srcId)}`,
      'DELETE',
    );
  }

  async extendReason(
    reasonId: number,
    params: { durationDays: number; note: string },
  ): Promise<QuiesceReason> {
    return this.mutate(
      `/quiesced-findings/reasons/${reasonId}/extend`,
      'POST',
      {
        duration_days: params.durationDays,
        note: params.note,
      },
    );
  }

  async liftReason(reasonId: number): Promise<QuiesceReason> {
    return this.mutate(`/quiesced-findings/reasons/${reasonId}`, 'DELETE');
  }

  async listQuiesceReasons(
    filters: QuiesceReasonsListFilters,
  ): Promise<QuiesceReason[]> {
    const result = await this.post<{ data: QuiesceReason[] }>(
      '/quiesced-findings/reasons/list',
      filters,
    );
    return result.data;
  }

  async getReasonLinks(reasonId: number): Promise<QuiesceFindingLink[]> {
    const result = await this.get<{ data: QuiesceFindingLink[] }>(
      `/quiesced-findings/reasons/${reasonId}/links`,
    );
    return result.data;
  }

  async getReasonHistory(reasonId: number): Promise<QuiesceEvent[]> {
    const result = await this.get<{ data: QuiesceEvent[] }>(
      `/quiesced-findings/reasons/${reasonId}/history`,
    );
    return result.data;
  }

  async getQuiescedFindings(
    filters: QuiescedFindingsFilters,
  ): Promise<PaginatedResponse<QuiescedFinding>> {
    return this.post('/quiesced-findings', filters);
  }

  async exportQuiescedFindingsCsv(
    filters: QuiescedFindingsFilters,
  ): Promise<void> {
    await this.downloadCsv(
      '/quiesced-findings/export',
      filters,
      'quiesced_findings.csv',
    );
  }

  async whoAmI(): Promise<CallerIdentity> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/quiesced-findings/whoami`,
    );
    if (response.status === 503) {
      throw new QuiesceServiceUnconfiguredError();
    }
    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }
}
